import React from 'react';
import { Check, ShieldCheck, Sparkles, Award, Clock } from 'lucide-react';

/**
 * <TrustChip /> — universal GIWAHS trust primitive (D-067).
 * -----------------------------------------------------------------
 * The single source of truth for every "this is verified" mark across
 * the platform — supplier cards, factory cards, RFQ responses, Founding
 * Partner pages, Trust Centre references, Founders Wall.
 *
 * Inherits the D-060 brand lock palette:
 *   - Brand black plate  #000000
 *   - Brand gold accent  #DAA520
 *   - Brand silver       #C0C0C0
 *   - Tagline gold       #A87A2F
 *
 * States:
 *   - verified            silver  — neutral, baseline verification
 *   - trusted             gold    — stronger track record
 *   - elite-verified      filled  — top-tier verification (gold-on-black)
 *   - founding-partner    crown   — exclusive, accepts founderNumber
 *   - under-review        clock   — in-process, neutral grey
 *
 * Sizes:
 *   - xs   (h-5,  text-[10px]) inline metadata · table cell · breadcrumb
 *   - sm   (h-6,  text-[11px]) default · card chip
 *   - md   (h-8,  text-xs)     hero/header
 *
 * Variants:
 *   - solid   (default)      filled background
 *   - outline                transparent background, coloured border
 *   - inverse                dark plate, light text (for light hero cards)
 *
 * Behaviour:
 *   - Optional href turns the chip into an anchor; otherwise renders <span>.
 *   - aria-label + tooltip prop both supported for a11y.
 *   - founderNumber prop adds a "· #021" suffix on the founding-partner state.
 *   - All states pass WCAG AA contrast.
 */

const STATES = {
  'verified': {
    label: 'Verified',
    Icon: Check,
    aria: 'Verified by GIWAHS Trust Centre',
    solid:    { bg: 'bg-[#1F1F1F]', fg: 'text-[#C0C0C0]', border: 'border-[#3A3A3A]', dot: '#C0C0C0' },
    outline:  { bg: 'bg-transparent', fg: 'text-[#525252]', border: 'border-[#C0C0C0]', dot: '#525252' },
    inverse:  { bg: 'bg-white', fg: 'text-[#0A0A0A]', border: 'border-[#0A0A0A]', dot: '#737373' },
  },
  'trusted': {
    label: 'Trusted',
    Icon: ShieldCheck,
    aria: 'Trusted supplier with verified track record',
    solid:    { bg: 'bg-[#1A1404]', fg: 'text-[#DAA520]', border: 'border-[#3A2A0A]', dot: '#DAA520' },
    outline:  { bg: 'bg-transparent', fg: 'text-[#A87A2F]', border: 'border-[#DAA520]', dot: '#DAA520' },
    inverse:  { bg: 'bg-[#FEF3C7]', fg: 'text-[#704A0A]', border: 'border-[#A87A2F]', dot: '#A87A2F' },
  },
  'elite-verified': {
    label: 'Elite Verified',
    Icon: Sparkles,
    aria: 'Elite Verified — top-tier independent verification',
    solid:    { bg: 'bg-[#DAA520]', fg: 'text-[#0A0A0A]', border: 'border-[#A87A2F]', dot: '#0A0A0A' },
    outline:  { bg: 'bg-transparent', fg: 'text-[#A87A2F]', border: 'border-[#DAA520]', dot: '#DAA520' },
    inverse:  { bg: 'bg-[#0A0A0A]', fg: 'text-[#DAA520]', border: 'border-[#DAA520]', dot: '#DAA520' },
  },
  'founding-partner': {
    label: 'Founding Partner',
    Icon: Award,
    aria: 'GIWAHS Founding Partner',
    solid:    { bg: 'bg-[#0A0A0A]', fg: 'text-[#DAA520]', border: 'border-[#DAA520]', dot: '#DAA520' },
    outline:  { bg: 'bg-transparent', fg: 'text-[#DAA520]', border: 'border-[#DAA520]', dot: '#DAA520' },
    inverse:  { bg: 'bg-[#DAA520]', fg: 'text-[#0A0A0A]', border: 'border-[#0A0A0A]', dot: '#0A0A0A' },
  },
  'under-review': {
    label: 'Under Review',
    Icon: Clock,
    aria: 'Verification under review — evidence pending',
    solid:    { bg: 'bg-[#171717]', fg: 'text-[#A3A3A3]', border: 'border-[#404040]', dot: '#737373' },
    outline:  { bg: 'bg-transparent', fg: 'text-[#737373]', border: 'border-[#A3A3A3]', dot: '#A3A3A3' },
    inverse:  { bg: 'bg-[#F5F5F5]', fg: 'text-[#525252]', border: 'border-[#A3A3A3]', dot: '#737373' },
  },
};

const SIZES = {
  xs: { wrap: 'h-5 px-2 gap-1 text-[10px]',  icon: 10, dot: 'h-1 w-1' },
  sm: { wrap: 'h-6 px-2.5 gap-1.5 text-[11px]', icon: 12, dot: 'h-1.5 w-1.5' },
  md: { wrap: 'h-8 px-3 gap-2 text-xs',      icon: 14, dot: 'h-2 w-2' },
};

export const TrustChip = ({
  state = 'verified',
  variant = 'solid',
  size = 'sm',
  founderNumber = null,
  founderTier = null,
  internal = false,
  href = null,
  tooltip = null,
  withDot = false,
  label = null,
  className = '',
  testId,
  ...rest
}) => {
  const cfg = STATES[state] || STATES['verified'];
  const variantStyles = cfg[variant] || cfg.solid;
  const sz = SIZES[size] || SIZES.sm;
  const { Icon } = cfg;

  // D-080 · Public TrustChips communicate trust and status only. Founder
  // tier-specific labels and #NNN numbers are gated to `internal` opt-in
  // (certificates, Founders Wall, admin records, founder dashboard). Public
  // chips always render the generic "Founding Partner" mark.
  const TIER_LABELS = { gold: 'Gold Founder', elite: 'Elite Founder', enterprise: 'Enterprise Founder' };
  const showTier  = internal && state === 'founding-partner' && founderTier && TIER_LABELS[founderTier];
  const showNum   = internal && state === 'founding-partner' && founderNumber != null;
  const tierLabel = showTier ? TIER_LABELS[founderTier] : null;

  const text = label || tierLabel || cfg.label;
  const ariaLabel = tooltip || (tierLabel ? `GIWAHS ${tierLabel}${showNum ? ' · #' + String(founderNumber).padStart(3, '0') : ''}` : cfg.aria);

  const inner = (
    <>
      {withDot && (
        <span
          aria-hidden="true"
          className={`shrink-0 rounded-full ${sz.dot}`}
          style={{ background: variantStyles.dot }}
        />
      )}
      <Icon size={sz.icon} strokeWidth={1.8} aria-hidden="true" className="shrink-0" />
      <span className="font-mono-data uppercase tracking-[0.14em] leading-none whitespace-nowrap">
        {text}
      </span>
      {showNum && (
        <span
          data-testid={testId ? `${testId}-number` : undefined}
          className="font-mono-data tracking-[0.1em] leading-none whitespace-nowrap opacity-90"
        >
          · #{String(founderNumber).padStart(3, '0')}
        </span>
      )}
    </>
  );

  const baseClasses = `inline-flex items-center rounded-full border ${variantStyles.bg} ${variantStyles.fg} ${variantStyles.border} ${sz.wrap} transition-colors ${className}`;
  const commonProps = {
    'data-testid': testId,
    'data-trust-state': state,
    'data-trust-variant': variant,
    'aria-label': ariaLabel,
    title: tooltip || ariaLabel,
  };

  if (href) {
    return (
      <a href={href} className={`${baseClasses} hover:opacity-80`} {...commonProps} {...rest}>
        {inner}
      </a>
    );
  }
  return <span className={baseClasses} {...commonProps} {...rest}>{inner}</span>;
};

export const TRUST_STATES = Object.keys(STATES);
export const TRUST_VARIANTS = ['solid', 'outline', 'inverse'];
export const TRUST_SIZES = Object.keys(SIZES);
export const FOUNDER_TIERS = ['gold', 'elite', 'enterprise'];

export default TrustChip;
